import { useState } from "react";
import { useQuery, useMutation } from "@tanstack/react-query";
import { useParams, useLocation } from "wouter";
import { apiRequest, queryClient } from "@/lib/queryClient";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Skeleton } from "@/components/ui/skeleton";
import { useToast } from "@/hooks/use-toast";
import { DetailPage, DetailCard, DetailGrid, DetailItem } from "@/components/detail-page";
import { ActivityTab } from "@/components/activity-tab";
import { BusinessUnitsList, ProductsList } from "@/components/inline-entity-list";
import { CreateBusinessUnitDialog, CreateProductDialog } from "@/components/dialogs";
import { ImpersonateButton } from "@/components/impersonate-button";
import { useAuth } from "@/lib/auth-context";
import { Copy, Check, Bot, Loader2 } from "lucide-react";
import { COUNTRY_CURRENCY_MAP, COUNTRY_LABELS } from "@shared/schema";
import type { BaseCountry, BaseCurrency } from "@shared/schema";

interface Organization {
  id: string;
  name: string;
  slug: string;
  email: string | null;
  phone: string | null;
  status: string;
  apiKey: string;
  settings: Record<string, unknown> | null;
  createdAt: string;
  updatedAt: string;
}

export default function OrganizationDetail() {
  const { toast } = useToast();
  const { user } = useAuth();
  const params = useParams<{ id: string }>();
  const [, setLocation] = useLocation();
  const [isEditing, setIsEditing] = useState(false);
  const [editForm, setEditForm] = useState<Partial<Organization>>({});
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [apiKeyCopied, setApiKeyCopied] = useState(false);
  const [createCorpAdminOpen, setCreateCorpAdminOpen] = useState(false);
  const [createProductOpen, setCreateProductOpen] = useState(false);

  const { data: pm, isLoading } = useQuery<Organization>({
    queryKey: ["organization", params.id],
    queryFn: async () => {
      const response = await apiRequest("GET", `/api/organizations/${params.id}`);
      if (!response.ok) throw new Error("Failed to fetch program manager");
      return response.json();
    },
    enabled: !!params.id,
  });

  // Fetch counts for tab labels
  const { data: businessUnits } = useQuery<{ id: string }[]>({
    queryKey: ["pm-business-units", params.id],
    queryFn: async () => {
      const response = await apiRequest("GET", `/api/business-units?organizationId=${params.id}`);
      if (!response.ok) return [];
      return response.json();
    },
    enabled: !!params.id,
  });

  const { data: products } = useQuery<{ id: string }[]>({
    queryKey: ["pm-products", params.id],
    queryFn: async () => {
      const response = await apiRequest("GET", `/api/products?organizationId=${params.id}`);
      if (!response.ok) return [];
      return response.json();
    },
    enabled: !!params.id,
  });

  const updateMutation = useMutation({
    mutationFn: async (updates: Partial<Organization>) => {
      const response = await apiRequest("PATCH", `/api/organizations/${params.id}`, updates);
      if (!response.ok) throw new Error("Failed to update program manager");
      return response.json();
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["organization", params.id] });
      queryClient.invalidateQueries({ queryKey: ["organizations"] });
      setIsEditing(false);
      toast({ title: "Success", description: "Program manager updated successfully" });
    },
    onError: () => {
      toast({ title: "Error", description: "Failed to update program manager", variant: "destructive" });
    },
  });

  const deleteMutation = useMutation({
    mutationFn: async () => {
      const response = await apiRequest("DELETE", `/api/organizations/${params.id}`);
      if (!response.ok) throw new Error("Failed to delete program manager");
      return response.json();
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["organizations"] });
      toast({ title: "Success", description: "Program manager deleted successfully" });
      setLocation("/organization/view");
    },
    onError: () => {
      toast({ title: "Error", description: "Failed to delete program manager", variant: "destructive" });
    },
  });


  const syncProductMutation = useMutation({
    mutationFn: async () => {
      const response = await apiRequest("POST", "/api/admin/automation/sync-product", {
        organization_id: params.id,
      });
      if (!response.ok) {
        const err = await response.json().catch(() => ({}));
        const msg = typeof err.error === "string" ? err.error : "Sync failed";
        throw new Error(msg);
      }
      return response.json();
    },
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: ["pm-products", params.id] });
      queryClient.invalidateQueries({ queryKey: ["products"] });
      toast({
        title: `Product ${data.action}`,
        description: `"${data.product?.name}" synced from iServeU portal (${data.fields_synced} fields)`,
      });
    },
    onError: (error: Error) => {
      toast({ title: "Sync failed", description: error.message, variant: "destructive" });
    },
  });

  const handleEdit = () => {
    if (pm) {
      setEditForm({
        name: pm.name,
        email: pm.email || "",
        phone: pm.phone || "",
        status: pm.status,
        settings: {
          ...(pm.settings || {}),
          baseCountry: (pm.settings?.baseCountry as BaseCountry) || "AU",
          baseCurrency: (pm.settings?.baseCurrency as BaseCurrency) || "AUD",
        },
      });
      setIsEditing(true);
    }
  };

  const handleSave = () => {
    updateMutation.mutate(editForm);
  };

  const handleCancelEdit = () => {
    setIsEditing(false);
    setEditForm({});
  };

  const copyApiKey = async () => {
    if (pm?.apiKey) {
      await navigator.clipboard.writeText(pm.apiKey);
      setApiKeyCopied(true);
      setTimeout(() => setApiKeyCopied(false), 2000);
      toast({ title: "Copied", description: "API key copied to clipboard" });
    }
  };

  const getStatusBadge = (status: string) => {
    const variants: Record<string, "default" | "secondary" | "destructive" | "outline"> = {
      ACTIVE: "default",
      INACTIVE: "secondary",
      PENDING: "outline",
      SUSPENDED: "destructive",
    };
    const colors: Record<string, string> = {
      ACTIVE: "bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200",
    };
    return <Badge variant={variants[status] || "secondary"} className={colors[status]}>{status}</Badge>;
  };


  if (isLoading) {
    return (
      <div className="p-6 space-y-6">
        <Skeleton className="h-8 w-64" />
        <Skeleton className="h-64 w-full" />
      </div>
    );
  }

  if (!pm) {
    return (
      <div className="p-6">
        <p className="text-muted-foreground">Program manager not found</p>
      </div>
    );
  }

  const breadcrumbs = [
    { label: "Organizations", href: "/organization/view" },
    { label: pm.name },
  ];

  const tabs = [
    {
      id: "overview",
      label: "Overview",
      content: (
        <div className="space-y-6">
          {/* Basic Information */}
          <DetailCard title="Basic Information">
            <DetailGrid columns={2}>
              <DetailItem label="Name" value={pm.name} />
              <DetailItem label="Slug" value={pm.slug} />
              <DetailItem label="Email" value={pm.email || "-"} />
              <DetailItem label="Phone" value={pm.phone || "-"} />
            </DetailGrid>
          </DetailCard>

          {/* Status */}
          <DetailCard title="Status">
            <DetailGrid columns={2}>
              <DetailItem label="Status" value={getStatusBadge(pm.status)} />
            </DetailGrid>
          </DetailCard>

          {/* Regional Settings */}
          <DetailCard title="Regional Settings">
            <DetailGrid columns={2}>
              <DetailItem
                label="Base Country"
                value={COUNTRY_LABELS[(pm.settings?.baseCountry as BaseCountry) || 'AU'] || 'Australia'}
              />
              <DetailItem
                label="Base Currency"
                value={(pm.settings?.baseCurrency as string) || 'AUD'}
              />
            </DetailGrid>
          </DetailCard>

          {/* API Access */}
          <DetailCard title="API Access">
            <div className="space-y-2">
              <p className="text-sm text-muted-foreground">API Key</p>
              <div className="flex items-center gap-2">
                <code className="flex-1 text-xs bg-muted px-3 py-2 rounded break-all font-mono">
                  {pm.apiKey}
                </code>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={copyApiKey}
                >
                  {apiKeyCopied ? (
                    <Check className="h-4 w-4" />
                  ) : (
                    <Copy className="h-4 w-4" />
                  )}
                </Button>
              </div>
            </div>
          </DetailCard>

          {/* Record Information */}
          <DetailCard title="Record Information">
            <DetailGrid columns={2}>
              <DetailItem label="ID" value={<code className="text-xs bg-muted px-2 py-1 rounded">{pm.id}</code>} />
              <DetailItem label="Created" value={new Date(pm.createdAt).toLocaleString()} />
              <DetailItem label="Last Updated" value={new Date(pm.updatedAt).toLocaleString()} />
            </DetailGrid>
          </DetailCard>
        </div>
      ),
    },
    {
      id: "business-units",
      label: `Business Units (${businessUnits?.length || 0})`,
      content: (
        <BusinessUnitsList
          organizationId={pm.id}
          onCreate={() => setCreateCorpAdminOpen(true)}
        />
      ),
    },
    {
      id: "products",
      label: `Products (${products?.length || 0})`,
      content: (
        <div className="space-y-4">
          {/* Sync from iServeU (SUPER only) */}
          {user?.role === "SUPER" && (
            <div className="flex items-center justify-between p-4 border rounded-lg bg-muted/30">
              <div>
                <p className="text-sm font-medium">iServeU Portal Sync</p>
                <p className="text-xs text-muted-foreground">
                  {syncProductMutation.isPending
                    ? "Fetching product data from iServeU portal... This may take 1–3 minutes."
                    : "Import product configuration, BIN, limits, and velocity rules from the iServeU portal."}
                </p>
              </div>
              <Button
                size="sm"
                variant="outline"
                disabled={syncProductMutation.isPending}
                onClick={() => syncProductMutation.mutate()}
              >
                {syncProductMutation.isPending ? (
                  <Loader2 className="h-3.5 w-3.5 mr-1.5 animate-spin" />
                ) : (
                  <Bot className="h-3.5 w-3.5 mr-1.5" />
                )}
                {syncProductMutation.isPending ? "Syncing..." : "Sync from iServeU"}
              </Button>
            </div>
          )}

          <ProductsList
            organizationId={pm.id}
            onCreate={() => setCreateProductOpen(true)}
          />
        </div>
      ),
    },
    {
      id: "activity",
      label: "Activity",
      content: (
        <ActivityTab
          entityType="organization"
          entityId={pm.id}
          createdAt={pm.createdAt}
          updatedAt={pm.updatedAt}
        />
      ),
    },
  ];

  // Edit mode content
  const editContent = (
    <DetailCard title="Edit Organization">
      <div className="space-y-4">
        <div className="space-y-2">
          <Label htmlFor="name">Name</Label>
          <Input
            id="name"
            value={editForm.name || ""}
            onChange={(e) => setEditForm({ ...editForm, name: e.target.value })}
          />
        </div>
        <div className="space-y-2">
          <Label htmlFor="email">Email</Label>
          <Input
            id="email"
            type="email"
            value={editForm.email || ""}
            onChange={(e) => setEditForm({ ...editForm, email: e.target.value })}
          />
        </div>
        <div className="space-y-2">
          <Label htmlFor="phone">Phone</Label>
          <Input
            id="phone"
            value={editForm.phone || ""}
            onChange={(e) => setEditForm({ ...editForm, phone: e.target.value })}
          />
        </div>
        <div className="grid grid-cols-2 gap-4">
          <div className="space-y-2">
            <Label htmlFor="baseCountry">Base Country</Label>
            <Select
              value={(editForm.settings as Record<string, unknown>)?.baseCountry as string || "AU"}
              onValueChange={(v: string) => {
                const country = v as BaseCountry;
                const currencies = COUNTRY_CURRENCY_MAP[country];
                setEditForm({
                  ...editForm,
                  settings: {
                    ...(editForm.settings || {}),
                    baseCountry: country,
                    baseCurrency: currencies[0],
                  },
                });
              }}
            >
              <SelectTrigger><SelectValue /></SelectTrigger>
              <SelectContent>
                {(Object.entries(COUNTRY_LABELS) as [BaseCountry, string][]).map(([code, label]) => (
                  <SelectItem key={code} value={code}>{label} ({code})</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div className="space-y-2">
            <Label htmlFor="baseCurrency">Base Currency</Label>
            <Select
              value={(editForm.settings as Record<string, unknown>)?.baseCurrency as string || "AUD"}
              onValueChange={(v: string) =>
                setEditForm({
                  ...editForm,
                  settings: {
                    ...(editForm.settings || {}),
                    baseCurrency: v as BaseCurrency,
                  },
                })
              }
            >
              <SelectTrigger><SelectValue /></SelectTrigger>
              <SelectContent>
                {COUNTRY_CURRENCY_MAP[((editForm.settings as Record<string, unknown>)?.baseCountry as BaseCountry) || "AU"].map((cur) => (
                  <SelectItem key={cur} value={cur}>{cur}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        </div>
        <div className="space-y-2">
          <Label htmlFor="status">Status</Label>
          <Select
            value={editForm.status}
            onValueChange={(value) => setEditForm({ ...editForm, status: value })}
          >
            <SelectTrigger><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="ACTIVE">Active</SelectItem>
              <SelectItem value="INACTIVE">Inactive</SelectItem>
              <SelectItem value="SUSPENDED">Suspended</SelectItem>
            </SelectContent>
          </Select>
        </div>
        <div className="flex justify-end gap-2 pt-4">
          <Button variant="outline" onClick={handleCancelEdit}>Cancel</Button>
          <Button onClick={handleSave} disabled={updateMutation.isPending}>
            {updateMutation.isPending ? "Saving..." : "Save Changes"}
          </Button>
        </div>
      </div>
    </DetailCard>
  );

  return (
    <>
      <DetailPage
        breadcrumbs={breadcrumbs}
        title={pm.name}
        subtitle={pm.slug}
        badge={getStatusBadge(pm.status)}
        headerActions={
          <ImpersonateButton
            entityType="ORGANIZATION"
            entityId={pm.id}
            entityName={pm.name}
            variant="outline"
          />
        }
        tabs={isEditing ? undefined : tabs}
        onEdit={!isEditing ? handleEdit : undefined}
        onDelete={!isEditing ? () => setDeleteDialogOpen(true) : undefined}
        isDeleting={deleteMutation.isPending}
        deleteDialogOpen={deleteDialogOpen}
        onDeleteDialogChange={setDeleteDialogOpen}
        deleteDialogTitle="Delete Organization"
        deleteDialogDescription={`Are you sure you want to delete "${pm.name}"? This action cannot be undone and will also delete all associated corporate admins, products, and data.`}
        onDeleteConfirm={() => deleteMutation.mutate()}
        isEditing={isEditing}
      >
        {isEditing && editContent}
      </DetailPage>

      <CreateBusinessUnitDialog
        open={createCorpAdminOpen}
        onOpenChange={setCreateCorpAdminOpen}
        organizationId={pm.id}
      />

      <CreateProductDialog
        open={createProductOpen}
        onOpenChange={setCreateProductOpen}
        organizationId={pm.id}
      />
    </>
  );
}
